import { useState, useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import { Button, Modal, toast, ToggleButton } from '@uniquegood/realworld-studio-design';
import type { ThumbnailStyleId } from '@/@types/aiStyle';
import { AI_USAGE_GUIDE_URL } from '@/const';
import { DEFAULT_SELECTED_ID } from '@/constants/aiStyleThumbnails';
import {
  usePostRequest,
  useParamsWithMatchPath,
  useRequest,
  useModalState,
  usePageExit
} from '@/hooks';
import { aiStyleManager } from '@/utils/aiStyleUtils';
import Layout from './components/Layout';
import Thumbnail from './components/Tumbnail';
import ErrorTemplate from './components/ErrorTemplate';
import { ClickEventName, PageViewEventName, track } from '@/track';

function AiStyleConfig() {
  const { appId } = useParamsWithMatchPath();
  const history = useHistory();
  const { modal, openModal, closeModal } = useModalState();

  const [selectedId, setSelectedId] = useState<ThumbnailStyleId | null>(DEFAULT_SELECTED_ID);
  const [isBGMEnabled, setIsBGMEnabled] = useState<boolean>(false);

  // POST 요청 훅 설정
  const {
    trigger: createProject,
    isLoading: isCreating,
    error: createError
  } = usePostRequest(`/apps/${appId}/ai/projects`, { skipGlobalErrorToast: true });
  const { data: aiProductData, error: aiProductError } =
    useRequest<CommonResponseModel<AiProductResponseModel[]>>(`/api/products/ai`);
  const { mutate: creditRefetch } = useRequest<CommonResponseModel<AiProductResponseModel[]>>(
    `/apps/${appId}/credit`
  );

  // 컴포넌트 마운트 시 sessionStorage 확인 및 리다이렉트
  useEffect(() => {
    const sessionData = sessionStorage.getItem('aiStory');
    if (!sessionData) {
      allowExit();
      history.push(`/apps/${appId}`);
    }
  }, [appId, history]);

  // 페이지 이탈 시 session storage 정리 함수
  const clearSessionStorage = useCallback(() => {
    sessionStorage.removeItem('aiStory');
  }, []);

  // 페이지 이탈 경고 모달 열기 함수
  const openUnsavedChangesWarningModal = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      track.onPageViewMixpanel(PageViewEventName.view_newgame_ai_exit_modal, {
        channel_id: appId
      });
      openModal({
        title: '작성한 내용이 사라질 수 있어요',
        children: (
          <div>
            콘텐츠를 만드는 도중에 페이지를 벗어나면 저장되지 않아요.
            <br />
            정말 나가시겠어요?
          </div>
        ),
        onClose: () => {
          closeModal();
          resolve(false); // 모달이 닫히면 false 반환 (남아있기)
        },
        customActionButton: (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              type="basic"
              size="large"
              onClick={() => {
                closeModal();
                track.onClick({
                  clickEventName: ClickEventName.click_newgame_ai_exit_modal_exit,
                  params: {
                    channel_id: appId
                  }
                });
                clearSessionStorage();
                resolve(true); // 나가기 버튼 클릭 시 true 반환
              }}
            >
              나가기
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                track.onClick({
                  clickEventName: ClickEventName.click_newgame_ai_exit_modal_cancel,
                  params: {
                    channel_id: appId
                  }
                });
                closeModal();
                resolve(false); // 남아있기 버튼 클릭 시 false 반환
              }}
            >
              남아있기
            </Button>
          </div>
        )
      });
    });
  }, [openModal, closeModal, appId, clearSessionStorage]);

  // 페이지 이탈 시 실행할 로직
  const { allowExit } = usePageExit(
    () => {
      clearSessionStorage();
    },
    {
      onBeforeUnload: () => console.log('새로고침/브라우저 종료'),
      onVisibilityChange: () => console.log('탭 변경/브라우저 숨김'),
      onRouteChange: () => console.log('다른 페이지로 라우팅'),
      shouldPreventDefault: true,
      shouldBlockRouting: true,
      enabled: true,
      customConfirmModal: openUnsavedChangesWarningModal,
      exitPath: `/apps/${appId}`
    }
  );

  // 메모이제이션된 스타일 선택 데이터
  const styleSelection = useMemo(() => {
    return aiStyleManager.getStyleSelection(selectedId);
  }, [selectedId]);

  // 메모이제이션된 썸네일 목록
  const thumbnails = useMemo(() => {
    return aiStyleManager.getAllStyles();
  }, []);

  // 총 크레딧 가격 계산
  const getTotalCreditPrice = () => {
    const templatePrice =
      aiProductData?.data.find((product) => product.generateType === 'Template')?.creditPrice ?? 40;
    const templateWithBgmPrice =
      aiProductData?.data.find((product) => product.generateType === 'TemplateWithBgm')
        ?.creditPrice ?? 70;
    return isBGMEnabled ? templateWithBgmPrice : templatePrice;
  };

  // 컨텐츠 API 요청 핸들러
  const handleGenerateContent = async () => {
    try {
      // SessionStorage에서 aiStory 데이터 가져오기
      const sessionData = sessionStorage.getItem('aiStory');
      if (!sessionData) {
        return;
      }

      const storyData = JSON.parse(sessionData);

      // 선택된 스타일 정보 가져오기
      const { stylePrompt: imageStylePrompt, styleTitle: imageStyle } = styleSelection;

      // API 요청에 필요한 데이터 포맷팅
      const apiRequestData = {
        title: storyData.data.title,
        introduction: storyData.data.introduction,
        development: storyData.data.development,
        conclusion: storyData.data.conclusion,
        genre: storyData.data.genre,
        aiUseLogId: storyData.data.aiUseLogId,
        imageStylePrompt,
        generateBgm: isBGMEnabled
      };

      // Click Event - mixpanel
      track.onClick({
        clickEventName: ClickEventName.click_newgame_ai_styleconfig_create,
        params: {
          channel_id: appId,
          is_bgm_enabled: isBGMEnabled
        }
      });

      await createProject(apiRequestData);

      creditRefetch();

      // 제출 시에는 의도적인 페이지 이동이므로 allowExit을 호출하여 페이지 이탈 허용
      allowExit();
      // 세션스토리지에 AI 생성 완료 상태 저장
      sessionStorage.setItem('aiGenerationComplete', 'true');

      history.push(`/apps/${appId}`);
    } catch (error) {
      if (
        (createError?.response?.data as { errorTitle: string }).errorTitle === 'NotEnoughCredit'
      ) {
        toast({
          type: 'error',
          message: '크레딧이 모두 소진됐어요. 내일 다시 이용해 주세요.'
        });
      } else {
        toast({
          type: 'error',
          message:
            (createError?.response?.data as { errorTitle: string }).errorTitle ||
            '스토리 생성에 실패했습니다. 다시 시도해주세요.'
        });
      }
    }
  };

  useEffect(() => {
    track.onPageViewMixpanel(PageViewEventName.view_newgame_ai_styleconfig, {
      channel_id: appId
    });
  }, [appId]);

  if (aiProductError) {
    return <ErrorTemplate />;
  }

  if (createError) {
    return <ErrorTemplate onRetry={handleGenerateContent} />;
  }

  return (
    <Layout
      title="원하는 스타일이 있으신가요?"
      description="마지막으로 스토리에 어울리는 스타일을 골라주세요"
      progress={100}
    >
      <div css={Container}>
        <div css={CardContainer}>
          <div css={CardHeader}>
            <div css={TitleGroup}>
              <h3 css={CardTitle}>이미지 스타일</h3>
              <p css={CardDescription}>선택한 스타일로 일관되게 이미지를 만들어요.</p>
            </div>
          </div>
          <div css={ThumbnailGrid}>
            {thumbnails.map((thumb) => (
              <Thumbnail
                key={thumb.id}
                title={thumb.title}
                image={thumb.image}
                isSelected={selectedId === thumb.id}
                onClick={() => setSelectedId(thumb.id)}
              />
            ))}
          </div>
        </div>
        {/* BGM Section */}
        <div css={BGMCard}>
          {/* 할인중 Chip - 할인이 있을 때만 노출 */}
          {(() => {
            const bgmPrice =
              aiProductData?.data.find((product) => product.generateType === 'Bgm')?.creditPrice ??
              10;
            const discountAmount = (() => {
              const templateWithBgm =
                aiProductData?.data.find((product) => product.generateType === 'TemplateWithBgm')
                  ?.creditPrice ?? 0;
              const template =
                aiProductData?.data.find((product) => product.generateType === 'Template')
                  ?.creditPrice ?? 0;
              return templateWithBgm - template;
            })();

            return discountAmount !== bgmPrice ? (
              <div css={DiscountChip}>여기서만 할인 중!</div>
            ) : null;
          })()}
          <div css={BGMHeader}>
            <div css={BGMTitleGroup}>
              <div css={BGMTitleRow}>
                <h3 css={BGMTitle}>BGM 생성하기</h3>
                <div css={BGMCredit}>
                  <span css={BGMCreditText(isBGMEnabled)}>(</span>
                  <div css={StarIcon}>
                    <div
                      css={css`
                        width: 16px;
                        height: 16px;
                        mask: url(/assets/icons/stars.svg) no-repeat center;
                        mask-size: contain;
                        background-color: ${isBGMEnabled ? '#7800ff' : '#555555'};
                        transition: background-color 0.3s ease;
                      `}
                    />
                  </div>
                  {/* 할인된금액, TemplateWithBgm, Template값이 하나라도 없다면 Bgm값 노출
                   */}
                  <span css={BGMCreditNumber(isBGMEnabled)}>
                    {(() => {
                      const templateWithBgm = aiProductData?.data.find(
                        (product) => product.generateType === 'TemplateWithBgm'
                      )?.creditPrice;
                      const template = aiProductData?.data.find(
                        (product) => product.generateType === 'Template'
                      )?.creditPrice;

                      if (templateWithBgm === undefined || template === undefined) {
                        return (
                          aiProductData?.data.find((product) => product.generateType === 'Bgm')
                            ?.creditPrice ?? 0
                        );
                      }

                      return templateWithBgm - template;
                    })()}
                  </span>
                  {/* 할인전금액, TemplateWithBgm - Template값이 Bgm값과 같다면 노출하지 않음 */}
                  {(() => {
                    const bgmPrice =
                      aiProductData?.data.find((product) => product.generateType === 'Bgm')
                        ?.creditPrice ?? 10;
                    const discountAmount = (() => {
                      const templateWithBgm =
                        aiProductData?.data.find(
                          (product) => product.generateType === 'TemplateWithBgm'
                        )?.creditPrice ?? 0;
                      const template =
                        aiProductData?.data.find((product) => product.generateType === 'Template')
                          ?.creditPrice ?? 0;
                      return templateWithBgm - template;
                    })();

                    return discountAmount !== bgmPrice ? (
                      <span css={BGMCreditBeforeDiscountText}>{bgmPrice}</span>
                    ) : null;
                  })()}
                  <span css={BGMCreditText(isBGMEnabled)}>)</span>
                </div>
              </div>
              <p css={BGMDescription}>스토리에 어울리는 음악을 자동으로 만들어요.</p>
            </div>
            <ToggleButton
              checked={isBGMEnabled}
              onChange={(e) => setIsBGMEnabled(e.target.checked)}
            />
          </div>
        </div>
        <div css={NoticeContainer}>
          <ul css={NoticeList}>
            <li css={NoticeItem}>
              AI가 생성한 미션과 퀘스트의 완성도가 떨어질 수 있으니, 반드시 검수 후 콘텐츠를 공개해
              주세요.
            </li>
            <li css={NoticeItem}>
              AI 생성 결과물(이미지, BGM 등)은 개인/상업용 모두 사용 가능해요.{' '}
              <a
                href={AI_USAGE_GUIDE_URL}
                target="_blank"
                rel="noopener noreferrer"
                css={NoticeLink}
              >
                (이용 안내)
              </a>
            </li>
          </ul>
        </div>

        <div css={ButtonContainer}>
          <Button
            type="primary"
            size="large"
            onClick={handleGenerateContent}
            disabled={!selectedId || isCreating} // 스타일이 선택되지 않거나 로딩 중이면 비활성화
          >
            {isCreating ? (
              '스토리 생성중...'
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>콘텐츠 생성하기</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div
                    css={css`
                      width: 16px;
                      height: 16px;
                      mask: url(/assets/icons/stars.svg) no-repeat center;
                      mask-size: contain;
                      background-color: ${!selectedId || isCreating ? '#a9a9a9' : 'white'};
                      transition: background-color 0.3s ease;
                    `}
                  />
                  <span>{getTotalCreditPrice()}</span>
                </div>
              </div>
            )}
          </Button>
        </div>
      </div>
      <Modal {...modal} />
    </Layout>
  );
}

export default AiStyleConfig;

const Container = css`
  max-width: 690px;
  margin: 0 auto;
  padding: 0 16px;
`;

const CardContainer = css`
  background-color: #ffffff;
  border-radius: 16px;
  border: 1px solid #ededed;
  padding: 24px;
  position: relative;
  box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.07);
`;

const CardHeader = css`
  margin-bottom: 12px;
`;

const TitleGroup = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CardTitle = css`
  font-family: 'Pretendard', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #0e1017;
  margin: 0;
`;

const CardDescription = css`
  font-family: 'Pretendard', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #626262;
  margin: 0;
`;

const ThumbnailGrid = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
`;

// BGM Section Styles
const BGMCard = css`
  background-color: #ffffff;
  border-radius: 16px;
  border: 1px solid #ededed;
  padding: 24px;
  margin-top: 24px;
  position: relative;
  box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.07);
`;

const BGMHeader = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
`;

const BGMTitleGroup = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

const BGMTitleRow = css`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BGMTitle = css`
  font-family: 'Pretendard', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #0e1017;
  margin: 0;
`;

const BGMCredit = css`
  display: flex;
  align-items: center;
`;

const BGMCreditText = (isEnabled: boolean) => css`
  font-family: 'Pretendard', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${isEnabled ? '#7800ff' : '#555555'};
  transition: color 0.3s ease;
`;

const BGMCreditBeforeDiscountText = () => css`
  font-family: 'Pretendard', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #b2b6c2;
  text-decoration-line: line-through;
  transition: color 0.3s ease;
  margin: 0 2px;
`;

const StarIcon = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
`;

const BGMCreditNumber = (isEnabled: boolean) => css`
  font-family: 'Pretendard', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${isEnabled ? '#7800ff' : '#555555'};
  transition: color 0.3s ease;
`;

const BGMDescription = css`
  font-family: 'Pretendard', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #626262;
  margin: 0;
  transition: color 0.3s ease;
`;

const ButtonContainer = css`
  display: flex;
  justify-content: center;
  margin-top: 24px;
  margin-bottom: 24px;
  width: 100%;
`;

const NoticeContainer = css`
  margin-top: 32px;
`;

const NoticeList = css`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  text-align: center;
`;

const NoticeItem = css`
  color: #626262;
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  position: relative;
  padding-left: 12px;
  max-width: 100%;
  width: fit-content;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #626262;
  }
`;

const NoticeLink = css`
  color: #626262;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 400;
  white-space: nowrap;

  &:hover {
    color: #626262;
  }
`;

const DiscountChip = css`
  position: absolute;
  top: -8px;
  background-color: #905bff;
  color: white;

  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px;

  padding: 4px 8px;
  border-radius: 100px;
  z-index: 10;
`;
